import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

/* Keeping an installed app current. A home-screen app resumes the page already in memory instead
   of reloading it, sometimes for days, so it never picks up a deploy on its own and its data is
   only as fresh as the last refetch. Here: pull down (or tap the current tab again) to refresh,
   a banner when a newer build is live, and a reload when a screen's code is gone after a deploy. */

/** This build's id, stamped by scripts/build-version.ts; "dev" outside a build. */
export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? "dev";

const reloadPage = () => window.location.reload();

export interface UpdateChecker {
  /** Asks the server which build is deployed; true once it differs from the one running. */
  check(): Promise<boolean>;
  available(): boolean;
  subscribe(fn: () => void): () => void;
}

export function createUpdateChecker({
  current,
  enabled = true,
  url = "/version.json",
  fetcher = (u, init) => fetch(u, init),
}: {
  current: string;
  enabled?: boolean;
  url?: string;
  fetcher?: (url: string, init: RequestInit) => Promise<Response>;
}): UpdateChecker {
  let available = false;
  const subs = new Set<() => void>();
  return {
    async check() {
      if (!enabled || available) return available;
      try {
        const res = await fetcher(url, { cache: "no-store" });
        const body = res.ok ? ((await res.json()) as { build?: unknown }) : {};
        if (typeof body.build === "string" && body.build !== current) {
          available = true;
          subs.forEach((fn) => fn());
        }
      } catch {
        /* offline or mid-deploy: ask again next time */
      }
      return available;
    },
    available: () => available,
    subscribe(fn) {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

/** The app's own checker: only a production build has a /version.json to compare against. */
export const updates = createUpdateChecker({ current: BUILD_ID, enabled: import.meta.env.PROD });

/** A refresh: a newer build reloads the whole app (new code and fresh data at once); otherwise
    the data on screen is fetched again. */
export async function refreshApp(
  refetch: () => Promise<unknown>,
  { checker = updates, reload = reloadPage }: { checker?: UpdateChecker; reload?: () => void } = {},
) {
  if (await checker.check()) reload();
  else await refetch();
}

/** [refreshing, refresh]: one refresh at a time, shared by the pull gesture and the tab bar. */
export function useRefresh(
  refetch: () => Promise<unknown>,
  opts?: { checker?: UpdateChecker; reload?: () => void },
): [boolean, () => Promise<void>] {
  const [refreshing, setRefreshing] = useState(false);
  const busy = useRef(false);
  const latest = useRef({ refetch, opts });
  latest.current = { refetch, opts };
  const refresh = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setRefreshing(true);
    try {
      await refreshApp(() => latest.current.refetch(), latest.current.opts);
    } finally {
      busy.current = false;
      setRefreshing(false);
    }
  }, []);
  return [refreshing, refresh];
}

/** Tapping the tab you're already on: back to the top first, a refresh once there. */
export function scrollTopOrRefresh(refresh: () => unknown) {
  if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "smooth" });
  else void refresh();
}

const PULL_MAX = 96;
export const PULL_TRIGGER = 64;
/** How long a short pull takes to slide back up (the .ptr.back transition in styles.css). */
export const PULL_BACK_MS = 200;

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** Pull down from the top of the page to refresh. Installed apps have no browser pull-to-refresh,
    so this is the app's own. Not while a sheet is open or the page is scrolled, and not from
    anything marked data-no-pull (a drag handle). */
export function PullToRefresh({ refreshing, onRefresh }: { refreshing: boolean; onRefresh: () => unknown }) {
  const [pull, setPull] = useState(0);
  // A short pull, let go: the indicator slides back up before it goes.
  const [back, setBack] = useState(false);
  const latest = useRef({ refreshing, onRefresh });
  latest.current = { refreshing, onRefresh };

  useEffect(() => {
    let startY: number | null = null;
    let dist = 0;
    let backTimer: number | undefined;
    const reset = () => {
      if (startY !== null && dist > 0 && !reducedMotion()) {
        setBack(true);
        window.clearTimeout(backTimer);
        backTimer = window.setTimeout(() => setBack(false), PULL_BACK_MS);
      }
      startY = null;
      dist = 0;
      setPull(0);
    };
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      const from = e.target instanceof Element ? e.target : null;
      if (
        e.touches.length !== 1 ||
        !t ||
        latest.current.refreshing ||
        window.scrollY > 0 ||
        document.querySelector(".modal-back") ||
        from?.closest("[data-no-pull]")
      )
        return;
      startY = t.clientY;
      window.clearTimeout(backTimer);
      setBack(false);
    };
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (startY === null || !t) return;
      if (window.scrollY > 0) return reset();
      dist = Math.min(PULL_MAX, Math.max(0, (t.clientY - startY) * 0.5));
      setPull(dist);
    };
    const onEnd = () => {
      if (startY !== null && dist >= PULL_TRIGGER) {
        void latest.current.onRefresh();
        dist = 0; // held where it is by the refresh, not slid back
      }
      reset();
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", reset);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", reset);
      window.clearTimeout(backTimer);
    };
  }, []);

  if (!refreshing && pull === 0 && !back) return null;
  const y = refreshing ? PULL_TRIGGER : pull;
  return createPortal(
    <div
      className={`ptr${refreshing ? " on" : ""}${pull >= PULL_TRIGGER ? " ready" : ""}${back && !refreshing ? " back" : ""}`}
      style={{ transform: `translate(-50%, ${y}px)` }}
      role="status"
      aria-label={refreshing ? "Refreshing" : "Pull to refresh"}
    >
      <span style={refreshing ? undefined : { transform: `rotate(${pull * 4}deg)` }}>
        <Icon name="refresh" />
      </span>
    </div>,
    document.body,
  );
}

const CHECK_EVERY = 10 * 60_000;

/** "A new version is ready": shown once a newer build is deployed. Checked each time the app comes
    back to the foreground and every few minutes while it's open. Never reloads by itself, so a
    half-filled form isn't lost. */
export function UpdateBanner({
  checker = updates,
  reload = reloadPage,
}: {
  checker?: UpdateChecker;
  reload?: () => void;
}) {
  const available = useSyncExternalStore(checker.subscribe, checker.available);
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === "visible") void checker.check();
    };
    document.addEventListener("visibilitychange", check);
    const timer = window.setInterval(check, CHECK_EVERY);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(timer);
    };
  }, [checker]);
  if (!available) return null;
  return createPortal(
    <div className="update-bar" role="status">
      <span>A new version is ready.</span>
      <button type="button" className="btn sm primary" onClick={reload}>
        Reload
      </button>
    </div>,
    document.body,
  );
}

const STALE_KEY = "corecrm:stale-reload";

/** After a deploy, an app still running the old build asks for screens' code files that are no
    longer on the server. Reload into the new build instead of showing an error, but only once
    in a while: if the reload didn't fix it, the error shows. Returns the unsubscribe. */
export function reloadOnStaleChunks({
  reload = reloadPage,
  storage,
  now = Date.now,
}: { reload?: () => void; storage?: Storage; now?: () => number } = {}) {
  const onError = (e: Event) => {
    try {
      const store = storage ?? window.sessionStorage; // reading it can throw where storage is blocked
      if (now() - (Number(store.getItem(STALE_KEY)) || 0) < 30_000) return;
      store.setItem(STALE_KEY, String(now()));
    } catch {
      return; // nowhere to note the attempt: a reload could loop
    }
    e.preventDefault();
    reload();
  };
  window.addEventListener("vite:preloadError", onError);
  return () => window.removeEventListener("vite:preloadError", onError);
}
